import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function CompanySettings({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { authFetch('/api/companies').then(r=>r.json()).then(d=>setCompanies(Array.isArray(d)?d:[])); }, [authFetch]);

  useEffect(() => {
    if (!selCompany) return;
    authFetch(`/api/companies/${selCompany}`).then(r=>r.json()).then(d=>{
      setCompany(d);
      setForm({ name:d.name||'', industry:d.industry||'', size:d.size||'', website:d.website||'',
        anonymous_mode: !!d.anonymous_mode,
        whitelabel_name: d.whitelabel_name||'', whitelabel_color: d.whitelabel_color||'#00d4ff' });
    });
  }, [selCompany, authFetch]);

  const save = async e => {
    e.preventDefault(); setSaving(true);
    const res = await authFetch(`/api/companies/${selCompany}`, { method:'PUT', body:JSON.stringify(form) });
    const d = await res.json();
    if (res.ok) {
      setCompany(d);
      // Re-sync form state from the server's confirmed response — without this,
      // the form inputs keep showing whatever was locally typed even after a
      // page reload or company switch would show genuinely different data,
      // and any field the server normalized (e.g. empty string vs null) would
      // silently drift from what's actually persisted.
      setForm({ name:d.name||'', industry:d.industry||'', size:d.size||'', website:d.website||'',
        anonymous_mode: !!d.anonymous_mode,
        whitelabel_name: d.whitelabel_name||'', whitelabel_color: d.whitelabel_color||'#00d4ff' });
      showToast('Settings saved');
    }
    else showToast(d.error||'Save failed', 'error');
    setSaving(false);
  };

  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';
  const INDUSTRIES = ['Technology','Manufacturing','Retail','Financial Services','Healthcare','Education','Professional Services','Real Estate','FMCG','Other'];
  const SIZES = ['1–10','11–50','51–200','201–500','500–1000','1000+'];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Company Settings</div>
        <div className="page-sub">Configure assessment behaviour, anonymous mode, and white-label branding</div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <label className="form-label">Select Company</label>
        <select className="form-select" style={{ maxWidth:320 }} value={selCompany} onChange={e=>setSelCompany(e.target.value)}>
          <option value="">Choose company...</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {company && (
        <form onSubmit={save}>
          {/* Basic info */}
          <div className="sec-header"><div className="sec-title">Company Info</div><div className="sec-line"></div></div>
          <div className="card" style={{ marginBottom:20 }}>
            <div className="g2" style={{ gap:14 }}>
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input className="form-input" value={form.website} onChange={e=>setForm({...form,website:e.target.value})} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label className="form-label">Industry</label>
                <select className="form-select" value={form.industry} onChange={e=>setForm({...form,industry:e.target.value})}>
                  <option value="">Select...</option>
                  {INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Size</label>
                <select className="form-select" value={form.size} onChange={e=>setForm({...form,size:e.target.value})}>
                  <option value="">Select...</option>
                  {SIZES.map(s=><option key={s} value={s}>{s} employees</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Assessment settings */}
          <div className="sec-header"><div className="sec-title">Assessment Settings</div><div className="sec-line"></div></div>
          <div className="card" style={{ marginBottom:20 }}>
            <div className="flag-row">
              <div style={{ flex:1 }}>
                <div className="flag-key">Anonymous Mode</div>
                <div className="flag-desc">Respondent names and emails are not stored. Only department and level are kept. All individual responses appear as "Anonymous".</div>
              </div>
              <div className={`toggle ${form.anonymous_mode ? 'on' : ''}`} onClick={()=>setForm({...form,anonymous_mode:!form.anonymous_mode})} />
            </div>
            {form.anonymous_mode && (
              <div style={{ padding:'10px 14px', background:'rgba(34,197,94,0.06)', borderRadius:8, border:'1px solid rgba(34,197,94,0.2)', marginTop:10 }}>
                <div style={{ fontSize:12, color:'var(--text2)' }}>
                  ✓ <strong style={{ color:'var(--green)' }}>Anonymous mode active</strong> — respondents will see a privacy notice on the assessment form. No names collected.
                </div>
              </div>
            )}
          </div>

          {/* White-label */}
          <div className="sec-header">
            <div className="sec-title">White-Label Branding</div>
            <div className="sec-line"></div>
            {!isPro && <span className="tag" style={{ color:'var(--blue)', borderColor:'rgba(201,151,58,0.3)' }}>🔒 Pro</span>}
          </div>
          <div className="card" style={{ marginBottom:24, opacity: isPro ? 1 : 0.6 }}>
            {!isPro && (
              <div style={{ padding:'10px 14px', background:'rgba(201,151,58,0.04)', borderRadius:8, border:'1px solid rgba(0,212,255,0.15)', marginBottom:16 }}>
                <span style={{ fontSize:12, color:'var(--text2)' }}>Upgrade to Pro to customize the assessment form with your company branding. </span>
                <span className="auth-link" style={{ fontSize:12 }} onClick={()=>navigate('/billing')}>Upgrade →</span>
              </div>
            )}
            <div className="g2" style={{ gap:14 }}>
              <div className="form-group">
                <label className="form-label">Brand Name (shown on assessment form)</label>
                <input className="form-input" value={form.whitelabel_name} disabled={!isPro}
                  onChange={e=>setForm({...form,whitelabel_name:e.target.value})}
                  placeholder="Your Company Name (leave blank for default)" />
              </div>
              <div className="form-group">
                <label className="form-label">Brand Color</label>
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={form.whitelabel_color||'#00d4ff'} disabled={!isPro}
                    onChange={e=>setForm({...form,whitelabel_color:e.target.value})}
                    style={{ width:44, height:36, border:'1px solid var(--border2)', borderRadius:6, background:'var(--bg3)', padding:2, cursor:isPro?'pointer':'not-allowed' }} />
                  <span style={{ fontFamily:'var(--ff-m)', fontSize:12, color:'var(--text2)' }}>{form.whitelabel_color}</span>
                  <div style={{ width:80, height:8, borderRadius:4, background:form.whitelabel_color||'#00d4ff' }}></div>
                </div>
              </div>
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>
              Preview: Assessment header will show "{form.whitelabel_name || company.name}" with your brand color as the accent.
            </div>
          </div>

          <div style={{ display:'flex', gap:12 }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving...':'Save Settings'}</button>
            <button type="button" className="btn btn-ghost" onClick={()=>navigate('/assessment', { state: {companyId:selCompany,companyName:company.name} })}>Get Assessment Link →</button>
          </div>
        </form>
      )}
    </div>
  );
}
